import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import {
  OE_PROFILE_DEFINITIONS,
  OE_RING_MATERIAL_LABEL,
  type OeElastomerCode,
} from '@bqs/pricing';
import {
  cartLineKey,
  isOeSealCartLine,
  toApiPosition,
  type CartLine,
  type OeSealCartLine,
  type PricingPreview,
} from '../api/types.js';

const OE_ELASTOMER_LABEL: Record<OeElastomerCode, string> = {
  '04': 'EPDM',
  '13': 'NBR',
  '08': 'FKM',
};

/** G-ST-P/OE, ADR-0022: eine Zeile im Warenkorb steht fuer die ganze
 *  konfigurierte Dichtung -- die Anzeige leitet sich aus den Eingaben und der
 *  Profilkonfiguration ab, es gibt kein separat gepflegtes Anzeigefeld. */
function oeCartCard(line: OeSealCartLine): {
  dims: string;
  profile: string;
  ring: string;
} {
  const profil = OE_PROFILE_DEFINITIONS.find(
    (p) => p.profileArticleCode === line.profileArticleCode,
  );
  const elastomerLabel =
    profil === undefined ? '' : OE_ELASTOMER_LABEL[profil.elastomerCode];
  const profilBezeichnung =
    profil === undefined
      ? line.profileArticleCode
      : `${profil.profileWidthMm}×${profil.nominalBoreLabel}`;
  return {
    dims: `${line.targetInnerDiameterMm} × ${line.targetOuterDiameterMm} mm`,
    profile: `${elastomerLabel}, Profil ${profilBezeichnung}`,
    ring: `Stützring ${OE_RING_MATERIAL_LABEL[line.ringMaterial]}`,
  };
}

/**
 * Die uebernommenen Positionen und -- getrennt davon -- die Angebotsvorschau.
 *
 * Die Trennung ist Absicht: Die Auswahl ist preisfrei. Erst ein ausdruecklicher
 * Klick fragt Preise an, und die kommen dann vollstaendig aus der Engine.
 *
 * Diese Vorschau ist die einzige sichtbare Zwischenansicht im Angebotsworkflow
 * (siehe `QuotePanel`): sie zeigt bereits Positionen, Preise und Summen
 * vollstaendig -- ein zusaetzlicher, fast identischer Entwurfs-Container nach dem
 * Erstellen entfaellt bewusst.
 */
export function SelectionList({
  positions,
  freight,
  onFreightChange,
  onRemove,
  onClear,
  discountPercent,
}: {
  positions: readonly CartLine[];
  /**
   * Verpackung und Fracht Inland: ausschliesslich fuer interne Rollen
   * (Schnellangebot, siehe `QuickQuotePage`). Nur wenn **beide**, `freight` und
   * `onFreightChange`, gesetzt sind, zeigt diese Ansicht das Eingabefeld und
   * sendet eine Ueberschreibung mit. Der Kundenworkflow (`CustomerApp`) laesst
   * beide bewusst weg: Ein Kontobenutzer darf Verpackung und Fracht weder
   * eingeben noch ueberschreiben, dafuer gilt ausschliesslich der globale
   * Vorgabewert (ADR-0015). Die API weist einen trotzdem mitgeschickten Wert
   * fuer diese Rollen ohnehin zurueck (`QuotesController`), dieses Weglassen
   * verhindert nur, dass die Oberflaeche ihn ueberhaupt anbietet.
   */
  freight?: string;
  onFreightChange?: (value: string) => void;
  onRemove: (key: string) => void;
  onClear: () => void;
  /**
   * Schnellangebot fuer interne Rollen: der frei vergebene Rabattsatz fuer die
   * Vorschau, ohne Konto. Ein Kontobenutzer bekommt seinen Rabatt immer aus der
   * eigenen Kondition und braucht dieses Feld nicht -- weggelassen bedeutet
   * unveraendertes Verhalten.
   */
  discountPercent?: number;
}): React.JSX.Element {
  const [preview, setPreview] = useState<PricingPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Ein leerer Warenkorb kann zu keiner vorhandenen Vorschau mehr gehoeren --
   * weder zu einer gerade geleerten Auswahl noch zu Positionen, die soeben in
   * ein ausgestelltes Angebot uebernommen wurden (siehe `QuotePanel`/
   * `CustomerApp`: der Warenkorb wird dort erst nach erfolgreichem Ausstellen
   * geleert, nie vorher). Der State wird hier wirklich genullt, nicht nur die
   * Anzeige bedingt ausgeblendet -- ein spaeterer Klick auf "Angebotsvorschau"
   * fragt ohnehin neu ab, und bis dahin darf keine veraltete Vorschau mehr
   * sichtbar oder verwendbar sein.
   */
  useEffect(() => {
    if (positions.length === 0) setPreview(null);
  }, [positions.length]);

  async function loadPreview(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<PricingPreview>('/api/pricing/preview', {
        positions: positions.map(toApiPosition),
        ...(freight !== undefined && freight.trim() !== ''
          ? { packagingFreightOverride: freight.trim() }
          : {}),
        ...(discountPercent !== undefined ? { discountPercent } : {}),
      });
      setPreview(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <aside className="card selection">
        <h3>Uebernommene Positionen</h3>
        {positions.length === 0 && <p className="muted">Noch nichts uebernommen.</p>}
        {positions.length > 0 && (
          <>
            <ul className="positions">
              {positions.map((p) =>
                isOeSealCartLine(p) ? (
                  <li key={cartLineKey(p)} className="oe-cart-card">
                    <div className="oe-cart-card-header">
                      <strong>G-ST-P/OE – Sonderdichtung</strong>
                      <button
                        type="button"
                        className="link"
                        onClick={() => onRemove(cartLineKey(p))}
                        aria-label="G-ST-P/OE-Dichtung entfernen"
                      >
                        entfernen
                      </button>
                    </div>
                    {/* Dichtlippe und Stuetzring sind hier bewusst NICHT einzeln
                        bearbeitbar -- eine konfigurierte Dichtung ist eine Einheit,
                        siehe OeSealCartLine. */}
                    <div>{oeCartCard(p).dims}</div>
                    <div>{oeCartCard(p).profile}</div>
                    <div>{oeCartCard(p).ring}</div>
                    <span className="qty-badge">Menge: {menge(p.quantity)} Stück</span>
                  </li>
                ) : (
                  <li key={cartLineKey(p)}>
                    <code>{p.articleNumber}</code>
                    <span className="qty-badge">{menge(p.quantity)} Stk.</span>
                    <button
                      type="button"
                      className="link"
                      onClick={() => onRemove(cartLineKey(p))}
                      aria-label={`${p.articleNumber} entfernen`}
                    >
                      entfernen
                    </button>
                  </li>
                ),
              )}
            </ul>
            {onFreightChange !== undefined && (
              <div className="freight">
                <label htmlFor="freight">Verpackung und Fracht Inland (€)</label>
                <input
                  id="freight"
                  type="text"
                  inputMode="decimal"
                  className="qty"
                  placeholder="z. B. 50.00"
                  value={freight ?? ''}
                  onChange={(e) => onFreightChange(e.target.value)}
                />
                <p className="note">
                  Kein globaler Vorgabewert hinterlegt: Der Betrag wurde nie festgelegt
                  und wird hier nicht erfunden. Ohne Angabe lehnen Vorschau und Ausstellen
                  ab.
                </p>
              </div>
            )}
            <div className="row">
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() => void loadPreview()}
              >
                Angebotsvorschau
              </button>
              <button type="button" onClick={onClear}>
                Leeren
              </button>
            </div>
          </>
        )}

        {error !== null && <p role="alert">{error}</p>}
      </aside>

      {preview !== null && (
        <section className="card preview-panel">
          <h4>Angebotsvorschau zum {preview.effectiveDate}</h4>
          <p className="note">
            Noch kein verbindliches Angebot. Alle Werte stammen aus der Preisengine.
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Artikel</th>
                  <th scope="col">Menge</th>
                  <th scope="col">Listenpreis</th>
                  <th scope="col">Rabatt</th>
                  <th scope="col">Netto/Stk.</th>
                  <th scope="col">Positionswert</th>
                </tr>
              </thead>
              <tbody>
                {preview.items.map((item, i) => (
                  <tr key={`${item.articleNumber}-${i}`}>
                    <th scope="row">
                      <code>{item.articleNumber}</code>
                    </th>
                    {item.status === 'ok' ? (
                      <>
                        <td className="num">{menge(item.quantity ?? '')}</td>
                        <td className="num">{item.listPrice} €</td>
                        <td className="num">{item.discountPercent} %</td>
                        <td className="num">{item.netUnitPrice} €</td>
                        <td className="num">{item.lineNet} €</td>
                      </>
                    ) : (
                      <td colSpan={5} className="muted">
                        {erklaerung(item.status)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <dl className="totals">
            <dt>Warenwert</dt>
            <dd>{preview.goodsValue} €</dd>
            {preview.minOrderSurchargeApplies && (
              <>
                <dt>
                  Mindermengenzuschlag
                  <span className="note">
                    {' '}
                    (unter {preview.appliedMinOrderThreshold} €)
                  </span>
                </dt>
                <dd>{preview.minOrderSurcharge} €</dd>
              </>
            )}
            <dt>Verpackung und Fracht Inland</dt>
            <dd>
              {preview.packagingFreightKnown ? (
                `${preview.packagingFreightAmount} €`
              ) : (
                // Nicht "0,00 €": Es ist kein Betrag hinterlegt, und das ist etwas
                // anderes als ein Betrag von null.
                <span className="muted">nicht hinterlegt</span>
              )}
            </dd>
            <dt className="total">Gesamt netto</dt>
            <dd className="total">{preview.documentTotalNet} €</dd>
          </dl>
        </section>
      )}
    </>
  );
}

/**
 * Menge zur Anzeige.
 *
 * Die API liefert Dezimalzahlen mit fester Nachkommastelle ("40.000"). Bedeutungslose
 * Nullen werden entfernt, der Wert selbst bleibt unangetastet.
 */
function menge(wert: string): string {
  return wert.includes('.') ? wert.replace(/\.?0+$/, '') : wert;
}

/** Positionsfehler im Klartext. Die Engine liefert Codes, nicht Saetze. */
function erklaerung(status: string): string {
  switch (status) {
    case 'article_unknown':
      return 'Artikel im Stamm nicht gefunden.';
    case 'no_price_list_version':
      return 'Zum Stichtag gibt es keine aktive Preislistenversion.';
    case 'no_price_in_version':
      return 'Kein Listenpreis in der aktiven Preislistenversion.';
    case 'no_condition_version':
      return 'Zum Stichtag ist fuer dieses Konto keine Kondition hinterlegt.';
    default:
      return `Nicht kalkulierbar (${status}).`;
  }
}
